import {
  AIChatBotImg,
  GourmetGalleryImg,
  HTreharneJonesImg,
  InTechJobsImg,
  SnakeImg,
  SpookSpottersImg,
  CCImg,
} from "../../Assets/Images/Projects";
import sdgDark from "../../Assets/Images/Branding/sdg-dark.png";
import CarbonVideo from "../../Assets/Videos/CarbonCalcVideo.mp4";
import CarbonCode from "../../Assets/Images/Code/CarbonCalcCode.png";
import ChatBotCode from "../../Assets/Images/Code/ChatBotCode.png";
import HTJCode from "../../Assets/Images/Code/HTJCode.png";

const projects = [
  {
    id: "ADHDPlanner",
    title: "In Development: Next-Gen ADHD Planner",
    image: sdgDark,
    description: [
      "I'm building a digital planner designed specifically for ADHD brains. After trying countless productivity apps that felt too rigid and overwhelming, I decided to build a tool that actually works for me.",
      "The project focuses on low-friction task management, dynamic energy tracking, and dopamine-friendly progress indicators rather than forcing strict scheduling.",
      "It's a full-stack MERN application featuring Google Calendar integration, currently in active development as I refine the core features.",
    ],
    siteUrl: "",
    githubFrontendUrl: "https://github.com/sdg877/tfmc-fe",
    githubBackendUrl: "https://github.com/sdg877/tfmc-be",
  },
  {
    id: "CarbonCalculator",
    title: "Carbon Tracker",
    image: CCImg,
    video: CarbonVideo,
    codeSnippets: [
      {
        image: CarbonCode,
        caption:
          "Next.js App Router logic using useEffect and fetch to integrate real-time environmental news APIs.",
      },
    ],
    description: [
      "Developed over six months, this production-standard full-stack application calculates and visualises personal carbon footprints. It features a bespoke Python calculation engine that transforms raw user data into CO2e metrics, tracking environmental impact trends over time.",
      "The frontend features a complex, dynamic React form supporting 20+ activity types, with conditional logic that adjusts fields based on user selection. Data is presented through interactive Recharts visualisations, comparing personal stats against global averages.",
      "A key technical challenge involved pivoting from a volunteering API to a curated real-time news feed. I engineered a backend proxy to fetch and sanitise NewsAPI data, using strict domain filtering and boolean search operators to eliminate 'noise' and ensure content relevancy.",
      "I also implemented a custom, self-managed authentication system. Below is a technical walkthrough and auth logic demonstration video.",
    ],
    siteUrl: "https://carbon-calculator-fe-pi.vercel.app/",
    githubFrontendUrl: "https://github.com/sdg877/carbon_calculator_fe",
    githubBackendUrl: "https://github.com/sdg877/carbon_calculator_be",
  },
  {
    id: "AIChatBot",
    title: "AI Chatbot",
    image: AIChatBotImg,
    codeSnippets: [
      {
        image: ChatBotCode,
        caption:
          "Asynchronous fetch logic in JavaScript for managing conversation IDs and persistent user sessions.",
      },
    ],
    description: [
      "Built in March 2025 using Flask, MongoDB, and the OpenAI API, this web app allows users to register, log in, and chat with an AI while storing and managing conversation history. It features a clean, responsive interface created with HTML, CSS, and JavaScript.",
      "Key features include user authentication, persistent chat history with renaming/deletion, a search function, smart subject generation, and a welcome message for new chats. Managing these involved careful handling of state and API responses.",
      "This project strengthened my skills in Flask, JavaScript, and API integration, and gave me hands-on experience with deployment and authentication. Planned improvements include real-time updates, improved search relevancy, and file uploads.",
    ],
    siteUrl: "https://chatbot-sdrakegill.pythonanywhere.com/",
    githubFrontendUrl: "https://github.com/sdg877/ai_chatbot",
    githubBackendUrl: "",
  },
  {
    id: "HTJ",
    title: "Artist's Portfolio",
    image: HTreharneJonesImg,
    codeSnippets: [
      {
        image: HTJCode,
        caption:
          "Dynamic data mapping and character encoding resolution using axios and decodeURIComponent.",
      },
    ],
    description: [
      "A bespoke freelance commission: a clean, professional portfolio site for an artist to showcase work, connect with customers, and manage commissions.",
      "The build includes an admin area, galleries, contact form, news section, and a commissions page. The client had a clear vision for the layout, colours, and banner image, and wanted to update content independently. I handled both design and development, ensuring responsive performance across devices.",
      "Key challenges included configuring the S3 bucket for uploads, handling mobile upload issues via nginx settings, and resolving character encoding errors with backend encryption and frontend decoding.",
      "This project sharpened my ability to explain technical concepts to non-technical clients and strengthened my project management skills. The site has no known bugs and gives the client full control over content updates.",
    ],
    siteUrl: "https://htreharnejones.co.uk/",
    githubFrontendUrl: "https://github.com/sdg877/hj_frontend",
    githubBackendUrl: "https://github.com/sdg877/hj_backend",
  },
  {
    id: "ProjectOne",
    title: "Confidential Project",
    image: sdgDark,
    description: [
      "A high-impact commercial project: I architected and delivered a production-ready MVP that served as the primary technical proof-of-concept for a successful £150k seed funding round. This mission-critical platform manages highly regulated, sensitive workflows for professional service providers.",
      "Engineered a sophisticated data architecture featuring 11 interconnected models. I designed complex relational logic to ensure seamless data integrity across automated scheduling, compliance monitoring, and audit trails.",
      "Developed a custom 'Traffic Light' prioritisation engine and a central calendar system. These features use dynamic date-trigger logic to automate task generation and visual urgency signalling, directly improving user adherence to strict regulatory deadlines.",
      "Key technical achievements included building a context-sensitive help system, an integrated email template engine with dynamic data-injection, and high-performance tables capable of aggregating data from multiple relational endpoints.",
    ],
    siteUrl: "",
    githubFrontendUrl: "",
    githubBackendUrl: "",
  },
  {
    id: "GourmetGallery",
    title: "Gourmet Gallery",
    image: GourmetGalleryImg,
    description: [
      "Gourmet Gallery is a full-stack MERN app where users can search, filter, and save recipes. It pulls data from an external recipe API and includes authentication for managing saved items.",
      "This was my first fully independent full-stack project. I built the backend with Express and MongoDB, added JWT-based authentication, and handled the frontend with React.",
      "Key challenges included integrating the API, managing auth flows, and handling inconsistent data. To improve layout consistency, I filtered out recipes lacking images.",
      "This project strengthened my problem-solving skills and boosted my confidence in full-stack development, API handling, and solo deployment.",
    ],
    siteUrl: "https://gourmetgallery.netlify.app/",
    githubFrontendUrl: "https://github.com/sdg877/recipe_site_fe",
    githubBackendUrl: "https://github.com/sdg877/recipe_site_be",
  },
  {
    id: "InTech",
    title: "inTech Jobs",
    image: InTechJobsImg,
    description: [
      "inTech Jobs is a MERN-stack job board built by a team of three, designed to help Junior Developers search, save, and track job applications.",
      "We integrated the Adzuna API to fetch listings using 'Junior' and 'Developer' keywords and added full CRUD functionality for managing saved jobs.",
      "I handled job data integration from the API, set up Google Maps for location display, and built auth protection to redirect unauthenticated users attempting to save jobs.",
      "This project strengthened my teamwork and Git workflow skills. We used Excalidraw for wireframes and Trello for task management to stay organised and deliver all planned features on time.",
    ],
    siteUrl: "https://intechjobs.netlify.app/",
    githubFrontendUrl: "https://github.com/sdg877/InTechFrontend",
    githubBackendUrl: "https://github.com/sdg877/InTechBackend",
  },
  {
    id: "SpookSpotters",
    title: "UK Spook Spotters",
    image: SpookSpottersImg,
    description: [
      "UK Spook Spotters was my Unit 2 bootcamp project: a full-stack app built with Node.js, Express, and MongoDB, where users can log paranormal sightings across the UK.",
      "Features include full CRUD functionality, Google OAuth for authentication, and restricted access for non-logged-in users. Users can record, search, and view detailed sightings.",
      "The backend uses Mongoose schemas for users, sightings, and locations. The frontend is built with Vue.js, designed for a clean, intuitive experience.",
      "A key challenge was implementing user permissions. I also fractured my dominant elbow two weeks before the deadline but still delivered the project on time.",
      "I gained confidence in debugging and strengthened my problem-solving skills. In future, I’d like to add a map feature and support for international sightings.",
    ],
    siteUrl: "https://unique-salmiakki-662491.netlify.app/",
    githubFrontendUrl: "https://github.com/sdg877/Project2Frontend",
    githubBackendUrl: "https://github.com/sdg877/Project2Backend",
  },
  {
    id: "Snake",
    title: "Snake",
    image: SnakeImg,
    description: [
      "This was my first coding project during General Assembly’s Software Engineering Bootcamp. I chose to build the classic Snake game using JavaScript, HTML, and CSS.",
      "The game challenges players to guide the snake to eat food, growing longer and faster, while avoiding collisions with walls or itself.",
      "This project strengthened my understanding of JavaScript fundamentals including event listeners, game loops, and collision detection.",
      "Additional features I added were a high score tracker, sound effects, and a custom alert to start the game.",
    ],
    siteUrl: "https://sdg877.github.io/Unit1_Project_Snake/",
    githubFrontendUrl: "https://github.com/sdg877/Unit1_Project_Snake",
    githubBackendUrl: "",
  },
];

export default projects;
